/**
 * The plugin's published versions, as a section of its page (Feature 70; it
 * was a dialog), with hard delete for any subset.
 *
 * Backend constraints surfaced by the UI:
 *   - The version that's currently installed cannot be deleted; the
 *     server returns 409, the row keeps an inline error and a
 *     disabled checkbox.
 *   - "Select all (except latest)" picks every non-latest version.
 *     The latest is kept, so this section can never empty the plugin;
 *     Delete plugin removes it whole.
 *   - The infra plugin is never uninstalled or deleted whole, but a
 *     version of it that is not installed is deleted like any other
 *     (optum-agent Feature 87, D6); its locked rows say why in its terms.
 *
 * Bulk delete fans out N parallel DELETE requests so partial failures
 * can be reported per row.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { ApiError } from '@optum/api-client/client';
import { deleteCatalogVersion, getCatalogEntry } from '@optum/api-client/endpoints/catalog';
import type { CatalogEntryOut, CatalogKind, CatalogVersion } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { DividedList } from '../../ui/DividedList.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { Checkbox } from '../../ui/Checkbox.js';
import { SectionCard, SectionCardBody } from '../../ui/SectionCard.js';

interface Props {
  kind: CatalogKind;
  /** Card snapshot — used for the initial render before the fresh fetch arrives. */
  entry: CatalogEntryOut;
}

interface RowState {
  selected: boolean;
  deleting: boolean;
  error: string | null;
}

export function VersionsSection({ kind, entry }: Props) {
  const qc = useQueryClient();
  const toast = useToast();

  const fresh = useQuery({
    queryKey: qk.catalogEntry(kind, entry.name),
    queryFn: () => getCatalogEntry(kind, entry.name),
    initialData: entry,
  });
  const data = fresh.data ?? entry;
  const versions = data.versions;
  const latestVersion = versions[0]?.version ?? data.latest_version;

  const [rowState, setRowState] = useState<Record<string, RowState>>({});

  // Reset selection state whenever the version list changes.
  useEffect(() => {
    setRowState(
      Object.fromEntries(
        versions.map((v) => [v.version, { selected: false, deleting: false, error: null }]),
      ),
    );
  }, [versions]);

  function patchRow(version: string, patch: Partial<RowState>) {
    setRowState((s) => {
      const prev: RowState = s[version] ?? {
        selected: false,
        deleting: false,
        error: null,
      };
      return { ...s, [version]: { ...prev, ...patch } };
    });
  }

  const selectedVersions = useMemo(
    () => versions.filter((v) => rowState[v.version]?.selected),
    [versions, rowState],
  );

  const allNonLatestSelected =
    versions.length > 1 &&
    versions.every((v) => v.version === latestVersion || rowState[v.version]?.selected);

  function toggleSelectAllExceptLatest() {
    const next = !allNonLatestSelected;
    setRowState((s) => {
      const out: Record<string, RowState> = { ...s };
      for (const v of versions) {
        if (v.version === latestVersion) continue;
        const prev: RowState = out[v.version] ?? {
          selected: false,
          deleting: false,
          error: null,
        };
        out[v.version] = { ...prev, selected: next };
      }
      return out;
    });
  }

  const deleteOne = useMutation({
    mutationFn: async (version: string) => {
      patchRow(version, { deleting: true, error: null });
      try {
        await deleteCatalogVersion({ kind, name: data.name, version });
        return version;
      } catch (err) {
        const message = err instanceof ApiError ? err.message : String(err);
        patchRow(version, { deleting: false, error: message });
        throw err;
      }
    },
    onSuccess: (version) => {
      toast.success(`Deleted ${data.name} ${version}`);
      qc.invalidateQueries({ queryKey: qk.catalogEntry(kind, data.name) });
      qc.invalidateQueries({ queryKey: ['catalog', kind] });
    },
    onError: (err) => {
      toast.error('Delete failed', err instanceof ApiError ? err.message : String(err));
    },
  });

  const deleteMany = useMutation({
    mutationFn: async () => {
      const targets = selectedVersions.map((v) => v.version);
      const results = await Promise.allSettled(
        targets.map(async (version) => {
          patchRow(version, { deleting: true, error: null });
          await deleteCatalogVersion({ kind, name: data.name, version });
          return version;
        }),
      );
      let done = 0;
      let failed = 0;
      results.forEach((r, i) => {
        const version = targets[i];
        if (version === undefined) return;
        if (r.status === 'fulfilled') {
          done++;
        } else {
          failed++;
          const reason = r.reason;
          const message = reason instanceof ApiError ? reason.message : String(reason);
          patchRow(version, { deleting: false, error: message });
        }
      });
      return { done, failed };
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.catalogEntry(kind, data.name) });
      qc.invalidateQueries({ queryKey: ['catalog', kind] });
    },
    onSuccess: ({ done, failed }) => {
      if (done > 0) {
        toast.success(`Deleted ${done} version${done === 1 ? '' : 's'}`);
      }
      if (failed > 0) {
        toast.error(`${failed} still present`, "Inspect each row for the server's reason.");
      }
    },
  });

  const busy = deleteOne.isPending || deleteMany.isPending;
  const selectedCount = selectedVersions.length;

  return (
    <SectionCard
      title="Versions"
      subtitle="Deleting removes the sdist from the private index and the catalog row. The installed version and the latest are kept."
    >
      <SectionCardBody>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            borderRadius: 'var(--radius-control)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-sunken)',
            padding: 11,
          }}
        >
          <Checkbox
            checked={allNonLatestSelected}
            onChange={toggleSelectAllExceptLatest}
            disabled={busy || versions.length <= 1}
            label="Select all (except latest)"
          />
          <div style={{ flex: '1 1 0%' }} />
          <Button
            type="button"
            variant="danger"
            size="sm"
            disabled={selectedCount === 0 || busy}
            onClick={() => deleteMany.mutate()}
          >
            Delete {selectedCount > 0 ? selectedCount : ''} version
            {selectedCount === 1 ? '' : 's'}
          </Button>
        </div>

        <DividedList
          style={{
            marginTop: 11,
            maxHeight: '420px',
            overflowY: 'auto',
            borderRadius: 'var(--radius-control)',
            border: '1px solid var(--border-hairline)',
          }}
        >
          {versions.map((v) => (
            <VersionRow
              key={v.version}
              version={v}
              isLatest={v.version === latestVersion}
              isInstalled={data.installed_version === v.version}
              isInfra={data.is_infra}
              state={rowState[v.version]}
              busy={busy}
              onToggle={(selected) => patchRow(v.version, { selected })}
              onDelete={() => deleteOne.mutate(v.version)}
            />
          ))}
        </DividedList>
      </SectionCardBody>
    </SectionCard>
  );
}

function VersionRow({
  version,
  isLatest,
  isInstalled,
  isInfra,
  state,
  busy,
  onToggle,
  onDelete,
}: {
  version: CatalogVersion;
  isLatest: boolean;
  isInstalled: boolean;
  isInfra: boolean;
  state: RowState | undefined;
  busy: boolean;
  onToggle: (selected: boolean) => void;
  onDelete: () => void;
}) {
  const selected = state?.selected ?? false;
  const deleting = state?.deleting ?? false;
  const error = state?.error ?? null;
  // The latest version is kept so Manage versions can never empty the
  // plugin — use Delete plugin for a full, permanent removal.
  const locked = isInstalled || isLatest;
  const lockReason = isInstalled
    ? isInfra
      ? 'Installed — install another version first'
      : 'Uninstall first to delete this version'
    : isLatest
      ? isInfra
        ? 'The latest version is kept — the platform installs it at boot'
        : 'The latest version is kept — delete the whole plugin instead'
      : undefined;
  return (
    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: 11 }}>
      <Checkbox
        checked={selected}
        onChange={(checked) => onToggle(checked)}
        disabled={busy || locked}
        aria-label={`Select ${version.version}`}
      />
      <div style={{ minWidth: 0, flex: '1 1 0%' }}>
        <p
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 'var(--text-meta-lg)',
            fontWeight: 'var(--weight-medium)',
          }}
        >
          <span className="mono">{version.version}</span>
          {isLatest && <Tag>Latest</Tag>}
          {isInstalled && <Tag tone="primary">Installed</Tag>}
          {version.yanked && <Tag tone="warning">Yanked</Tag>}
        </p>
        <p style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
          published {relativeTime(version.published_at)} · sha256{' '}
          <code title={version.sdist_sha256} className="mono">
            {version.sdist_sha256.slice(0, 12)}
          </code>
        </p>
        {error && (
          <p
            title={error}
            style={{
              marginTop: 5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 'var(--text-meta-lg)',
              color: 'var(--danger)',
            }}
          >
            {error}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onDelete}
        disabled={busy || locked}
        aria-label={`Delete ${version.version}`}
        title={lockReason ?? 'Delete'}
        style={{
          flexShrink: 0,
          borderRadius: 'var(--radius-control)',
          padding: 7,
          color: 'var(--text-muted)',
          background: 'transparent',
          border: 'none',
          ...(locked || busy ? { cursor: 'not-allowed', opacity: 0.4 } : { cursor: 'pointer' }),
        }}
      >
        {deleting ? '…' : '×'}
      </button>
    </li>
  );
}

function Tag({
  children,
  tone = 'neutral',
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'primary' | 'warning';
}) {
  const tint = {
    neutral: { background: 'var(--surface-fill)', color: 'var(--text-muted)' },
    primary: { background: 'var(--accent-tint)', color: 'var(--text-accent)' },
    warning: { background: 'var(--warn-tint)', color: 'var(--warn)' },
  }[tone];
  return (
    <span
      style={{
        display: 'inline-block',
        borderRadius: 'var(--radius-pill)',
        padding: '5px 7px',
        fontSize: 'var(--text-meta-lg)',
        fontWeight: 'var(--weight-medium)',
        ...tint,
      }}
    >
      {children}
    </span>
  );
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}
